import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { useTenantLabels } from "../lib/labels";
import { APPROVALS_COMING_SOON } from "../lib/approvals-flag";
import { useT, t as i18nT } from "../lib/i18n";
import { createPortal } from "react-dom";
import { alpha, Btn, Icon, Modal, T } from "@v0y4ge/jrni-ui";
import { useNav } from "./router";
import { can, useUser, navHiddenForRole, useTenant, signOut, portraitSrc } from "./access";
import { navProfileForUser } from "./navProfiles";
import { useNavComposition } from "../lib/tenant-vocab";
import { getNavPins, setNavPins } from "@v0y4ge/api-client";
import { messagingLandingRoute } from "../screens/messaging/messaging-visibility";
import { matchesNavQuery } from "./nav-search";

/**
 * Left rail (IconRail) — the Pinned+All navigation (Side Rail exploration
 * Concept C), THE primary nav for every persona since the 2026-08-06 cutover:
 * the user's pinned subset of the role-filtered catalog + the All-features
 * launcher. The classic grouped/expandable rail and the mobile hamburger
 * drawer were removed at cutover (changelog/2026-08-06T2030-new-nav-cutover.md).
 */

// rail id → route to navigate to when clicking it
/* Messaging resolves through `messagingLandingRoute()` rather than a literal:
   its rail tabs can be hidden (HIDDEN_TABS, 2026-09-04) and the first VISIBLE
   tab has to be where the rail item lands, or Messaging opens a screen whose
   tab is not in the rail. */
export const RAIL_ROUTES: Record<string, string> = {
  dashboard: "dashboard", calendar: "calendar.week", events: "events", guests: "guests",
  eventday: "eventday",
  surveys: "surveys",
  inbox: "inbox", campaigns: "campaigns", reports: "reports", approvals: "approvals",
  "cs-questions": "questions", "cs-services": "services", "cs-queues": "queues",
  /* Services lands on `services` — the ServicesLibrary family (2026-09-21, PP).
     It pointed at `ops.services` (WorkforceServiceCatalogue, `mode="config"`),
     which meant the only Services entry anyone could actually reach opened the
     family-tree catalogue, and `/services` — with its categories, packages and
     three editor screens — was reachable by deep link alone. Both Services rail
     items now agree on one destination rather than naming the same word for two
     different screens. `ops.services` / `ops.catalogue` still resolve, so a
     saved link or a rail= highlight keeps working. */
  "appt-book": "appointments", "appt-services": "services", promos: "promos", messaging: messagingLandingRoute(),
  "q-board": "queues", "q-console": "queues.console", "q-dash": "queues.dashboard",
  products: "products",
  tickets: "tickets", /* 2026-09-30 KP: ticket-groups v2 screens. 2026-10-02 Nick: no rail item any more (Events tab) — kept so deep links + stale pins resolve. */
  /* `staff` keeps its route (deep links, staff.edit) but has no rail item since
     2026-08-10 — Staff & Roles (`people`) is the single entry point. */
  locations: "locations", nodes: "nodes", people: "people", roles: "roles", staff: "staff", resources: "resources", branding: "branding",
  links: "landing.pages", settings: "settings.general",
  /* Event hubs (2026-08-10): the public multi-event catalogs. The screen has
     existed since the hub builder landed but was reachable only from search —
     no rail id pointed at it. */
  hubs: "hub.pages",
  /* Integrations (2026-08-07) — promoted from "search for it every time" to a
     first-class pinnable feature. Points at the EXISTING settings.integ route;
     no other item's route prefixes it (`settings` resolves to settings.general),
     so the one-highlight rule holds and settings.integ.detail lights this item
     alone. */
  integrations: "settings.integ",
  "wf-schedule": "workforce.schedule", "wf-forecast": "workforce.forecast", "wf-coverage": "workforce.coverage",
  "wf-staffing": "workforce.staffing", "wf-ops": "workforce.ops", "wf-requests": "workforce.requests",
  "wf-rules": "workforce.rules", "wf-myday": "workforce.myday",
  // Events-only My Day (2026-10-02): its own route so it never shares a highlight with wf-myday.
  "ev-myday": "events.myday",
  // Events-only My Calendar (2026-10-05): its own route so it never shares a highlight with wf-mysched.
  "ev-mycal": "events.mycalendar",
  // Working copy of My Day (2026-07-30) — its own route, so exactly one rail item
  // can match: the copy declares rail="wf-myday-copy" and My Day stays dark.
  "wf-myday-copy": "workforce.myday.sandbox",
  // Merged schedule + time (2026-07-31). Both the combined screen and the
  // standalone schedule route declare rail="wf-mysched", so one item lights.
  "wf-mysched": "workforce.myschedule",
  // My Skills & Services (2026-08-01) — its own screen, its own item.
  "wf-myskills": "workforce.myskills",
  "so-region": "storeops.region", "so-floor": "storeops.floor", "so-insights": "storeops.insights",
  "so-clients": "storeops.clients", "so-events": "storeops.events", "so-roster": "storeops.roster",
  "so-skills": "storeops.skills", "so-perf": "storeops.perf", "so-loyalty": "storeops.loyalty",
  "so-targets": "storeops.targets", "so-schedules": "storeops.schedules", "so-staff": "storeops.staff",
  "so-integrations": "storeops.integrations", "so-compliance": "storeops.compliance", "so-settings": "storeops.settings",
  "so-my-day": "storeops.my.day", "so-my-book": "storeops.my.book", "so-my-targets": "storeops.my.targets", "so-my-schedule": "storeops.my.schedule",
  // Bank six-persona port (P1, 2026-07-17) — profile-mode rail ids (shell/navProfiles.ts)
  "ab-setup": "workforce.setup", "wf-reporting": "workforce.reporting", "wf-payroll": "workforce.payroll",
  "wf-copilot": "workforce.copilot", "wf-runs": "workforce.runs", "wf-draft": "workforce.draft",
  "wf-advisors": "workforce.advisors", "wf-marketplace": "workforce.marketplace",
  "wf-compliance": "workforce.compliance", "wf-integration": "workforce.integration",
  "wf-planning": "workforce.planning", "wf-coaching": "workforce.coaching",
  "wf-assists": "workforce.assists", "wf-reqs": "workforce.requisitions",
  "wf-region": "workforce.region", "wf-region-compare": "workforce.region.compare",
  "wf-region-redistrib": "workforce.region.redistribute", "wf-region-runs": "workforce.region.runs",
  "wf-region-scope": "workforce.region.scope",
  "wf-floor": "workforce.floor",
  /* Calendar insertion phase 2 (2026-08-08, Greg): the Floor Calendar day board. */
  "floor-calendar": "floor.calendar",
  /* Region layer (2026-08-10): the Regional Calendar above it. */
  "region-calendar": "floor.region",
  "wf-myperf": "workforce.myperf", "wf-myrelationships": "workforce.myrelationships",
  /* REQ-128 (2026-08-03): the read-only catalogue view. Same screen Riley configures, no
     controls — mounted for the branch manager and the advisor. */
  "wf-services-view": "workforce.services.view",
  "wf-svc-coverage": "workforce.coverage.staff",
  "appt-catalogue": "ops.catalogue",
  // "wf-mytime" retired 2026-08-01: My Time IS "My Schedule & Time" now. Kept as
  // an ALIAS to the same route so any saved rail profile still resolves — it
  // points at the identical route as "wf-mysched", which the rail HARD RULE
  // explicitly allows (declared id owns the highlight; the alias resolves equal).
  "wf-mytime": "workforce.myschedule",
  "ab-today": "ops.today", "ab-walkins": "ops.walkins", "ab-services": "services",
  "ab-booking": "branding", "ab-branches": "locations", "ab-audit": "settings.audit",
  journeys: "journeys",
  /* New Journeys (2026-08-17): the unified Journey designer — plan v2 ruling ⑤,
     a NEW rail item; the legacy Journey builder above is untouched. */
  "new-journeys": "newjourneys",
  /* All Journeys (2026-09-03): the Appointment Page Designer as its own rail item. */
  "all-journeys": "alljourneys",
  /* Manage Pages (2026-09-09, operator): promoted OUT of the Journeys tab strip
     and onto the rail as its own module, beside Journey / Journey Builder /
     All Journeys. Being a rail id is what makes it pinnable — pins are stored
     as section ids (PIN_FALLBACK_ORDER, cdNavPins), so a surface that exists
     only as another section's tab can never be pinned. */
  "manage-pages": "confirmpages",
  customers: "customers",
  /* Unified administrator, Phase 1 (2026-08-05). `wf-section` is the ONE
     Workforce rail entry that replaces the eleven per-surface items on the old
     admin/workforce profiles — it opens the section index, which lists all 27
     workforce surfaces. `team-roles` promotes Team & roles out of a Settings
     tab and onto the rail: it is the administrator's defining job and was
     reachable from neither bank profile. */
  /* 2026-08-24 (operator): the Events rail entry goes STRAIGHT to the events
     list. The section index it used to open was a menu-of-menus in front of the
     one screen people actually wanted, and every surface it listed is either a
     tab on that list or its own rail item. The section index screen and its
     `events.hub` route are deleted (2026-08-24, with the wizard sweep). */
  "ev-section": "events",
  "wf-section": "workforce",
  // Payments admin (2026-08-04) — Orders ledger; Billing/Tax are tabs under Settings.
  orders: "orders",
};

interface RailItem { id: string; label: string; labelKey: string; tenantLabelKey?: string; svg: ReactNode; needs?: string | string[]; group?: string; comingSoon?: boolean; gated?: boolean; badge?: number; }

/* Profile-mode icon resolution (navProfiles carries string keys, not JSX). */
const PROFILE_ICONS: Record<string, ReactNode> = {
  grid: <Icon.grid />, calendar: <Icon.calendar />, check: <Icon.check />, warn: <Icon.warn />,
  chart: <Icon.chart />, users: <Icon.users />, clock: <Icon.clock />, refresh: <Icon.refresh />,
  network: <Icon.network />, user: <Icon.user />, ticket: <Icon.ticket />, list: <Icon.list />,
  send: <Icon.send />, msg: <Icon.msg />, tags: <Icon.tags />, layers: <Icon.layers />,
  help: <Icon.help />, csv: <Icon.csv />, loc: <Icon.loc />, palette: <Icon.palette />, settings: <Icon.settings />,
  journeys: <Icon.grid />,
  /* 2026-09-10: the served Roles item asks for `lock`; without an entry here a
     served icon key silently resolves to the grid glyph. */
  lock: <Icon.lock />,
};

/* REMOVING A FEATURE FROM NAVIGATION — both catalogs, or it stays visible.
 * The rail and the All-features launcher (pinned grid, RECENT, the group lists)
 * all render from ONE array, `items`, built in IconRail — but that array has two
 * possible sources: this RAIL_ITEMS catalog for the default grouped rail, and the
 * SERVED nav-composition profile (vocab-preview-seeds.js in the design preview,
 * @v0y4ge/api-mocks on the platform) for any persona with a matching profile.
 * Deleting from one leaves the item on the other's rail and in its launcher.
 * Served defaultPins / seed-world nav-pins.json should lose the id too: pins and
 * recents are filtered by catalog membership, so a stale id is harmless but a
 * pin seed that names a feature nobody can see is a lie in the data. */
const RAIL_ITEMS: RailItem[] = [
  { id: "dashboard", label: "Dashboard", labelKey: "shell.rail.dashboard", svg: <Icon.grid />, group: "my-day" },
  { id: "calendar", label: "Calendar", labelKey: "shell.rail.calendar", svg: <Icon.calendar />, needs: "events.read", group: "my-day" },
  { id: "eventday", label: "Event Day", labelKey: "shell.rail.eventday", svg: <Icon.check />, needs: ["onsite.walkup", "onsite.queue", "guests.checkin"], group: "my-day" },
  // Workforce process (2026-07-16). Cap-gated: events-only roles don't hold
  // workforce caps, so the group hides itself for them; System Administrator
  // and the workforce roles see their slice.
  /* Schedule retired from the rail (2026-09-14, KP): a staff member's hours are
     the "Hours only" layer of My calendar; managers roster on Floor. The
     workforce.schedule route stays reachable for deep links. */
  // { id: "wf-schedule", label: "Schedule", labelKey: "shell.rail.wfSchedule", svg: <Icon.grid />, needs: "schedules.read", group: "workforce" },
  { id: "wf-coverage", label: "Coverage", labelKey: "shell.rail.wfCoverage", svg: <Icon.warn />, needs: "demand.read", group: "workforce" },
  { id: "wf-forecast", label: "Forecast", labelKey: "shell.rail.wfForecast", svg: <Icon.chart />, needs: "demand.read", group: "workforce" },
  { id: "wf-staffing", label: "Event staffing", labelKey: "shell.rail.wfStaffing", svg: <Icon.users />, needs: "staffing.read", group: "workforce" },
  { id: "wf-ops", label: "Ops board", labelKey: "shell.rail.wfOps", svg: <Icon.clock />, needs: "wfops.read", group: "workforce" },
  /* Floor Calendar in the DEFAULT grouped rail (2026-08-13): the item previously
     existed ONLY in served nav-composition profiles (a branch-manager profile),
     so tenants without a served workforce profile — every persona on a tenant
     that has none — couldn't reach or even pin the screen: it was absent from
     the All-features catalog entirely. Same cap gate as the route
     (floor.calendar -> wfops.read in routeTable). */
  /* Calendar Control (2026-09-14, KP): one surface, four items, promoted into
     the grouped rail so every capability-holder reaches them — not only
     personas with a served profile. "Floor calendar" retired in the same pass:
     the day board is Floor's own mode now. */
  { id: "wf-myday", label: "My Day", labelKey: "shell.rail.wfMyday", svg: <Icon.calendar />, needs: "myschedule.read", group: "my-day" },
  /* Events-only My Day (2026-10-02): served to the three events roles' profiles. */
  { id: "ev-myday", label: "My Day", labelKey: "shell.rail.wfMyday", svg: <Icon.calendar />, needs: "events.read", group: "my-day" },
  /* Events-only My Calendar (2026-10-05): served to Event Manager and Event Coordinator. */
  { id: "ev-mycal", label: "My Calendar", labelKey: "shell.rail.wfMyschedtimeoff", svg: <Icon.calendar />, needs: "events.read", group: "my-day" },
  { id: "wf-floor", label: "Floor", labelKey: "shell.rail.wfFloor", svg: <Icon.clock />, needs: "wfops.read", group: "workforce" },
  { id: "wf-mysched", label: "My Calendar", labelKey: "shell.rail.wfMyschedtimeoff", svg: <Icon.calendar />, needs: "myschedule.read", group: "my-day" },
  /* Approvals (2026-09-14): the staff-facing request surfaces retire, the
     manager approval queue does not — gate moved swaps.read → swaps.approve. */
  { id: "wf-requests", label: "Approvals", labelKey: "shell.rail.wfRequests", svg: <Icon.refresh />, needs: "swaps.approve", group: "workforce" },
  { id: "wf-rules", label: "Labor rules", labelKey: "shell.rail.wfRules", svg: <Icon.check />, needs: "laborrules.read", group: "workforce" },
  // `wf-my` (My schedule) retired 2026-07-29 — unreachable for every persona in
  // every vertical; My Time owns week/open-shifts/requests. See the changelog.
  // Queues process (2026-07-17). Section functionality (Queues / Queue
  // Journeys / Display Boards / Archive) lives in TABS on the `queues` route
  // (Events-section paradigm), so the rail carries just the section entry and
  // the staff console. Cap-gated as ever.
  { id: "q-console", label: "Concierge", labelKey: "shell.rail.qConsole", svg: <Icon.check />, needs: "queue.serve", group: "queues" },
  { id: "q-board", label: "Queues", labelKey: "shell.rail.qBoard", svg: <Icon.list />, needs: "queues.read", group: "queues" },
  // Queue dashboard (2026-07-22) — location / region / org queuing performance.
  { id: "q-dash", label: "Queue dashboard", labelKey: "shell.rail.qDash", svg: <Icon.chart />, needs: "queuehealth.read", group: "queues" },
  // Appointments process (2026-07-26, APT-13 activation — one process, all
  // tenants). Services here = the APPOINTMENT services catalog (ops.services,
  // re-homed from the bank ops rail per operator D2); the events services
  // config (cs-services) stays under Configuration.
  { id: "appt-book", label: "Appointments", labelKey: "shell.rail.apptBook", svg: <Icon.clock />, needs: "appointments.read", group: "appointments" },
  { id: "appt-services", label: "Services", labelKey: "shell.rail.apptServices", svg: <Icon.layers />, needs: "services.read", group: "appointments" },
  // Store Ops process (2026-07-16 fidelity port) — `gated`: shown only when the SERVED nav composition enables it for this tenant (§1.2.4; no slug literals in source).
  { id: "so-region", label: "Region", labelKey: "shell.rail.soRegion", svg: <Icon.network />, needs: "storeops.region", group: "storeops", gated: true },
  { id: "so-floor", label: "Store floor", labelKey: "shell.rail.soFloor", svg: <Icon.grid />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-insights", label: "Executive Insights", labelKey: "shell.rail.soInsights", svg: <Icon.chart />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-clients", label: "Clienteling", labelKey: "shell.rail.soClients", svg: <Icon.user />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-events", label: "Event calendar", labelKey: "shell.rail.soEvents", svg: <Icon.calendar />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-roster", label: "Week roster", labelKey: "shell.rail.soRoster", svg: <Icon.calendar />, needs: "storeops.floorplan", group: "storeops", gated: true },
  { id: "so-skills", label: "Skills matrix", labelKey: "shell.rail.soSkills", svg: <Icon.users />, needs: "storeops.floorplan", group: "storeops", gated: true },
  { id: "so-perf", label: "Advisor performance", labelKey: "shell.rail.soPerf", svg: <Icon.chart />, needs: "storeops.floorplan", group: "storeops", gated: true },
  { id: "so-loyalty", label: "Loyalty tiers", labelKey: "shell.rail.soLoyalty", svg: <Icon.ticket />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-targets", label: "Sales targets", labelKey: "shell.rail.soTargets", svg: <Icon.chart />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-schedules", label: "Schedules", labelKey: "shell.rail.soSchedules", svg: <Icon.list />, needs: "storeops.floorplan", group: "storeops", gated: true },
  { id: "so-staff", label: "Staff", labelKey: "shell.rail.soStaff", svg: <Icon.users />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-integrations", label: "Integrations", labelKey: "shell.rail.soIntegrations", svg: <Icon.network />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-compliance", label: "Compliance", labelKey: "shell.rail.soCompliance", svg: <Icon.check />, needs: "storeops.read", group: "storeops", gated: true },
  { id: "so-settings", label: "Settings", labelKey: "shell.rail.soSettings", svg: <Icon.list />, needs: "storeops.manage", group: "storeops", gated: true },
  { id: "so-my-day", label: "My Day", labelKey: "shell.rail.soMyDay", svg: <Icon.calendar />, needs: "mybook.read", group: "storeops", gated: true },
  { id: "so-my-book", label: "My Book", labelKey: "shell.rail.soMyBook", svg: <Icon.user />, needs: "mybook.read", group: "storeops", gated: true },
  { id: "so-my-targets", label: "My Targets", labelKey: "shell.rail.soMyTargets", svg: <Icon.chart />, needs: "mybook.read", group: "storeops", gated: true },
  { id: "so-my-schedule", label: "My Schedule", labelKey: "shell.rail.soMySchedule", svg: <Icon.clock />, needs: "mybook.read", group: "storeops", gated: true },
  { id: "campaigns", label: "Campaigns", labelKey: "shell.rail.campaigns", svg: <Icon.send />, needs: "campaigns.read", group: "admin" },
  { id: "messaging", label: "Messaging", labelKey: "shell.rail.messaging", svg: <Icon.msg />, needs: "campaigns.read", group: "admin" },
  { id: "events", label: "Events", labelKey: "shell.rail.events", svg: <Icon.ticket />, needs: "events.read", group: "admin" },
  /* "tickets" removed from the catalog 2026-10-02 (Nick): Tickets is a TAB on the
     Events list (eventsSectionTabs → tickets), same as Event Hubs (2026-08-25).
     RAIL_ROUTES keeps the id; a stale pin stops rendering. */
  { id: "cs-services", label: "Services", labelKey: "shell.rail.csServices", svg: <Icon.layers />, needs: "services.read", group: "admin" },
  // Journey builder (2026-07-28). PROFILE_ICONS already carried a `journeys` key,
  // so the item existed only in a SERVED nav profile — tenants rendering the
  // default grouped rail had no Journey entry at all and could only deep-link.
  { id: "journeys", label: "Journey", labelKey: "shell.rail.journeys", svg: <Icon.grid />, needs: "journeys.read", group: "admin" },
  // New Journeys (2026-08-17) — the unified designer preview. Gated on
  // `journeys.read` since 2026-09-08: the old `newjourneys.read` is declared in
  // no catalog, so the row was hidden from every persona rather than shown to
  // admins. See routeTable.ts for the full note.
  { id: "new-journeys", label: "Journey Builder", labelKey: "shell.rail.newJourneys", svg: <Icon.layers />, needs: "journeys.read", group: "admin" },
  // All Journeys (2026-09-03) — the Appointment Page Designer (Appointments · Walk-Ins ·
  // Event List · Event Landing · Kiosk) ported 1:1 from the Gold Master prototype.
  // Added as a THIRD item beside Journey and Journey Builder (operator ruling); same
  // admin-only gate. Rendered for the Administrator profile of all three tenants.
  { id: "all-journeys", label: "All Journeys", labelKey: "shell.rail.allJourneys", svg: <Icon.grid />, needs: "journeys.read", group: "admin" },
  // Manage Pages (2026-09-09) — the booking-confirmation page library + designer,
  // promoted from a Journeys tab to its own module. Same admin gate as the three
  // journey items it sits with: the pages are authored by whoever authors journeys.
  { id: "manage-pages", label: "Manage Pages", labelKey: "shell.rail.managePages", svg: <Icon.list />, needs: "manage_pages.read", group: "admin" },
  { id: "products", label: "Products", labelKey: "shell.rail.products", svg: <Icon.tags />, needs: "products.read", group: "admin" },
  { id: "wf-services-view", label: "Branch services", labelKey: "shell.rail.wfServicesView", svg: <Icon.layers />, needs: "myschedule.read", group: "workforce" },
  { id: "wf-svc-coverage", label: "Service coverage", labelKey: "shell.rail.wfSvcCoverage", svg: <Icon.users />, needs: "advisors.read", group: "workforce" },
  { id: "cs-questions", label: "Booking questions", labelKey: "shell.rail.csQuestions", svg: <Icon.help />, needs: "questions.read", group: "admin" },
  { id: "surveys", label: "Surveys", labelKey: "shell.rail.surveys", svg: <Icon.csv />, needs: "surveys.read", group: "admin" },
  { id: "promos", label: "Coupons & Promos", labelKey: "shell.rail.promos", svg: <Icon.tags />, needs: "promos.read", group: "admin" },
  /* The old `guests` rail item is GONE (2026-09-10 customers drop): two rail
     entries both labelled with the tenant's customer noun, pointing at two
     different books of people, is the confusion the drop exists to end. The
     tenant-wide Guests CRM itself is untouched and stays reachable through the
     Events section's Attendees tab (list-composites.tsx), gated on
     `guests.read`; per-event guest lists remain inside their events. The
     matching `guests` entry in the platform STUDIO_RAIL_CATALOG is kit-owned
     and lands with CC's ingest. */
  /* ONE directory item, labelled with the tenant noun through the server-merged
     catalog (`common.customers`), gated on the capability the routes enforce.
     No `gated` flag: the item is no longer composed in per nav profile. */
  { id: "customers", label: "Customers", labelKey: "common.customers", svg: <Icon.users />, needs: "customers.read", group: "admin" },
  { id: "locations", label: "Locations", labelKey: "shell.rail.locations", svg: <Icon.loc />, needs: "locations.read", group: "admin" },
  /* Organisation structure (2026-08-24) — the node tree. A SEPARATE rail item
     rather than a tab under Locations, because they are two different stores of
     overlapping real-world objects with no reconciliation between them, and
     nesting one under the other would assert a relationship that does not exist.
     Same `locations.read` gate: the node kind/attribute routes sit on the
     `locations.*` capability family, so a role that can read Locations can read
     this. Without this item the screens were reachable only by URL. */
  { id: "nodes", label: "Organisation", labelKey: "shell.rail.nodes", svg: <Icon.network />, needs: "locations.read", group: "admin" }, /* LOCAL PATCH (platform side, 2026-09-08 full resync r3): was any-of locations.read/settings.read/workforce.admin/wfops.read (workforce.admin is declared nowhere); narrowed to the one capability the route is gated on */
  // People (2026-08-06) — the administrator's roster: users AND bookable staff
  // in one record. Same `team.read` gate as Staff and Team & roles, so it
  // appears for exactly the personas that already administer people.
  /* 2026-09-10 (operator): relabelled "Staff" and split from Roles below. */
  { id: "people", label: "Staff", labelKey: "shell.rail.staff", svg: <Icon.users />, needs: "team.read", group: "admin" },
  /* Roles (2026-09-10, operator) — role definition on its own route. Gated on
     `team.read`, the shell's projection of `identity.team.read` — the
     capability the role list operation declares. Creating, editing and deleting
     custom roles is gated separately on `team.write` (`identity.team.manage`)
     inside the screen. Same gate as Staff, so no persona gains reach it lacked. */
  { id: "roles", label: "Roles", labelKey: "shell.rail.roles", svg: <Icon.lock />, needs: "team.read", group: "admin" },
  /* "hubs" (Event hubs) removed from the catalog 2026-08-25 (operator): Event
     Hubs is a TAB on the Events list (eventsSectionTabs → hub.pages), so a
     second rail entry pointed at a screen already one click away inside the
     section it belongs to. The route stays in RAIL_ROUTES above so deep links
     and any saved pin still resolve; pins are filtered by catalog membership,
     so a stale "hubs" pin simply stops rendering. */
  { id: "resources", label: "Resources", labelKey: "shell.rail.resources", svg: <Icon.grid />, needs: "locations.read", group: "admin" },
  /* Approvals are Coming soon (CC 0224): no rail or All-features entry while the switch is on. */
  ...(APPROVALS_COMING_SOON ? [] : [{ id: "approvals", label: "Approvals", labelKey: "shell.rail.approvals", svg: <Icon.check />, needs: ["settings.write", "campaigns.approve"], group: "admin" }]),
  { id: "reports", label: "Reports", labelKey: "shell.rail.reports", svg: <Icon.chart />, needs: "reports.read", group: "admin" },
  // Payments admin (2026-08-04) — Orders / Transactions ledger. Cap-gated on
  // billing.read so only roles with the billing surface see it.
  { id: "orders", label: "Orders", labelKey: "shell.rail.orders", svg: <Icon.list />, needs: "billing.read", group: "admin" },
  { id: "branding", label: "Branding", labelKey: "shell.rail.branding", svg: <Icon.palette />, needs: "branding.read", group: "admin" },
  { id: "integrations", label: "Integrations", labelKey: "shell.rail.integrations", svg: <Icon.network />, needs: "integrations.read", group: "admin" },
  { id: "settings", label: "Settings", labelKey: "shell.rail.settings", svg: <Icon.settings />, needs: ["settings.read", "observability.read"], group: "admin" },
];

const RAIL_GROUP_LABELS: Record<string, string> = { "my-day": "Today", workforce: "Workforce", queues: "Queues", appointments: "Appointments", storeops: "Store Ops", admin: "Configuration" };

function _railCan(needs?: string | string[]): boolean {
  if (!needs) return true;
  if (Array.isArray(needs)) return needs.some((p) => can(p));
  return can(needs);
}

function railRoute(id: string): string | undefined {
  if (id === "settings" && !can("settings.read") && can("observability.read")) return "settings.api";
  return RAIL_ROUTES[id];
}

/** The route a catalog id opens — exported so the topbar's global search can
 *  build its feature rows from THIS catalog instead of its own list. */
export function navRailRoute(id: string): string | undefined {
  return railRoute(id);
}

/**
 * shell.rail.<id> resolution (CD-RAIL-I18N-NOTE-2026-07-20 / kit v4.3,
 * 0ee90d611-to-d9254edc): every rail item resolves through ONE path, no
 * per-item exceptions, per the note. `fallback` composes in the tenant-noun
 * override this rail already had (the "guests" item's `lbl("customers", ...)`
 * call) so it keeps working exactly as today underneath the new key.
 *
 * REQ-045 update (2026-07-20 night, kit v4.6): organizationId now flows
 * end-to-end — `access.ts` captures `whoami().organizationId` and calls
 * `lib/i18n.ts`'s `refreshTranslationCatalog(orgId)`, which layers the
 * server-merged (tenant + org) catalog on top of the sync bundled boot via
 * `getTranslationCatalog(locale, organizationId)`. So `shell.rail.*` keys
 * now pick up org-scoped overrides (e.g. a retail org's "Soirées"/
 * "Clientèle") automatically, with zero further code changes here.
 *
 * CORRECTION 2026-09-10 (customers-screen drop §"Naming"): the note that
 * used to sit here claimed no generic noun key existed — "checked `en.json`
 * — only the rail-specific `shell.rail.guests` key is there". That is stale
 * and was verified false: `common.customer` and `common.customers` are in
 * `packages/i18n-catalog/locales/*.json` for all shipped locales (en
 * "Customer"/"Customers", de "Kunde"/"Kunden"), and GuestProfile,
 * dashboard-components, Dashboard and ServiceEditorPage already read them.
 * The tenant override for the noun arrives through the server-merged catalog
 * (`getTranslationCatalog(locale, organizationId)`), so `t("common.customers", "Customers")`
 * is the whole resolution for a screen in the drop.
 *
 * `useTenantLabels` is NOT retired: `lbl()` still composes the pre-catalog
 * `labels` vocabulary for the call sites OUTSIDE this drop, and dropping it
 * would silently regress any tenant carrying a `labels` map rather than a
 * catalog override. It goes when those call sites move to the two keys. */
function railLabel(t: (key: string) => string, key: string, fallback: string): string {
  const r = t(key);
  return r === key ? fallback : r;
}

const RAIL_GROUP_KEYS: Record<string, string> = { "my-day": "groupMyDay", workforce: "groupWorkforce", queues: "groupQueues", appointments: "groupAppointments", storeops: "groupStoreOps", admin: "groupAdmin" };
function railGroupLabel(t: (key: string) => string, groupId: string): string | undefined {
  const fallback = RAIL_GROUP_LABELS[groupId];
  if (!fallback) return fallback;
  const camel = RAIL_GROUP_KEYS[groupId];
  if (!camel) return fallback;
  const key = `shell.rail.${camel}`;
  const r = t(key);
  return r === key ? fallback : r;
}

/* ── Pinned+All navigation — permanent since the 2026-08-06 cutover (Nick).
 * Ported from the Side Rail exploration via the flag-gated trial; the trial
 * flag (cdNewNav), the "Try New UI" pill, the classic grouped rail, and the
 * hamburger drawer are all REMOVED. Rulings (evaluation doc §6) still bind:
 * railHit selection is UNCHANGED — a route whose item isn't pinned lights
 * ZERO rail items (sanctioned-honest, not a regression); the launcher is
 * CHROME riding ?launcher=1 so Back closes it; no hubs, no recents. Served
 * nav-composition profiles carry `defaultPins` (curated per persona); the
 * user's pin EDITS are browser-local (`cdNavPins:*`) until getNavPins/
 * setNavPins ops land (follow-up REQ) — pins are preference, never authority:
 * caps + catalog membership filter them at render. */

/** Derived defaults for users with NO served profile (D6's honest fallback):
 * curated high-value order, filtered to what the role can actually see. */
const PIN_FALLBACK_ORDER = ["dashboard", "calendar", "appt-book", "q-board", "wf-mysched", "wf-myday", "wf-mytime", "reports", "events", "customers"];
/* Pins for a served profile: its own seed, else the curated order intersected
 * with what the profile actually carries, else — an override row served from
 * `tenant_nav_profiles`, whose columns hold no pins — the profile's own first
 * items. Without that last step a self-service rail rendered EMPTY (only
 * "Expand navigation" / "All features") until the person pinned something
 * themselves: the fallback order intersected none of its items. Docket
 * 2026-09-16, Ask 4. */
function defaultPinsFor(profile: { defaultPins?: string[] } | null | undefined, items: RailItem[]): string[] {
  if (profile?.defaultPins?.length) return profile.defaultPins;
  const curated = PIN_FALLBACK_ORDER.filter((id) => items.some((i) => i.id === id)).slice(0, 6);
  if (curated.length) return curated;
  return profile ? items.slice(0, 4).map((i) => i.id) : [];
}
function pinsKey(slug: string | null, profileKey: string | null): string { return `cdNavPins:${slug || "-"}:${profileKey || "default"}`; }
/* Pins are read under the ACTIVE slug and nothing else. A slug-to-slug map here
 * would be a per-tenant artifact in the platform tree, which this tree does not
 * carry in any form — including in a comment (CD docket 2026-09-09 §5). A
 * browser holding pins under a slug that no longer exists reads none and is
 * minted the served default, which is the correct answer for a renamed tenant. */
function loadPins(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown[]).filter((x): x is string => typeof x === "string") : null;
  } catch { return null; }
}
function savePins(key: string, ids: string[]): void { try { localStorage.setItem(key, JSON.stringify(ids)); } catch { /* private mode */ } }

/* ── Rail ergonomics preferences (2026-08-07) ─────────────────────────
 * Three browser-local preferences beside the pin cache: whether the rail is
 * collapsed, the user's order inside each launcher group, and the last five
 * features opened. Unlike pins (which ride served getNavPins/setNavPins) these
 * have no contract op — they are preference, never authority, so localStorage
 * is the whole story. Every read is defensive: a corrupt or absent value falls
 * back to the shipped default rather than throwing on boot. */
const RAIL_COLLAPSED_KEY = "cdRailCollapsed";
function loadCollapsed(): boolean {
  try { const v = localStorage.getItem(RAIL_COLLAPSED_KEY); return v === null ? true : v === "1"; } catch { return true; }
}
function saveCollapsed(v: boolean): void { try { localStorage.setItem(RAIL_COLLAPSED_KEY, v ? "1" : "0"); } catch { /* private mode */ } }

type GroupOrder = Record<string, string[]>;
/** `pKey` arrives already namespaced for pins (`cdNavPins:<tenant>:<profile>`);
 *  strip that prefix so these keys read `cdNavOrder:<tenant>:<profile>`. */
function prefScope(pKey: string): string { return pKey.replace(/^cdNavPins:/, ""); }
function orderKey(pKey: string): string { return `cdNavOrder:${prefScope(pKey)}`; }
function loadOrder(pKey: string): GroupOrder {
  try {
    const raw = localStorage.getItem(orderKey(pKey));
    if (!raw) return {};
    const v = JSON.parse(raw) as GroupOrder;
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    const out: GroupOrder = {};
    Object.keys(v).forEach((k) => { if (Array.isArray(v[k])) out[k] = v[k].filter((x): x is string => typeof x === "string"); });
    return out;
  } catch { return {}; }
}
function saveOrder(pKey: string, o: GroupOrder): void { try { localStorage.setItem(orderKey(pKey), JSON.stringify(o)); } catch { /* private mode */ } }

const RECENTS_MAX = 5;
function recentsKey(pKey: string): string { return `cdNavRecents:${prefScope(pKey)}`; }
function loadRecents(pKey: string): string[] {
  try { const raw = localStorage.getItem(recentsKey(pKey)); if (!raw) return []; const v = JSON.parse(raw); return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, RECENTS_MAX) : []; } catch { return []; }
}
function saveRecents(pKey: string, ids: string[]): void { try { localStorage.setItem(recentsKey(pKey), JSON.stringify(ids.slice(0, RECENTS_MAX))); } catch { /* private mode */ } }

/* One-time sweep of the double-prefixed keys these preferences wrote before
 * prefScope() landed (`cdNavRecents:cdNavPins:<tenant>:<role>`). Nothing reads
 * them; they are OUR OWN stale writes from earlier the same day, so clearing
 * them destroys no user state. Pins are never touched. */
(() => {
  try {
    Object.keys(localStorage)
      .filter((k) => /^cdNav(Recents|Order):cdNavPins:/.test(k))
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* private mode */ }
})();

/** Collapsed/expanded rail + the two launcher-list preferences, in one hook so
 *  IconRail and MobileBottomNav read identical state. */
function useNavPrefs(pKey: string) {
  const [collapsed, setCollapsedState] = useState<boolean>(() => loadCollapsed());
  const [order, setOrderState] = useState<GroupOrder>(() => loadOrder(pKey));
  const [recents, setRecentsState] = useState<string[]>(() => loadRecents(pKey));
  useEffect(() => { setOrderState(loadOrder(pKey)); setRecentsState(loadRecents(pKey)); }, [pKey]);
  const setCollapsed = (v: boolean) => { setCollapsedState(v); saveCollapsed(v); };
  const reorderGroup = (groupId: string, ids: string[]) => {
    setOrderState((o) => { const next = { ...o, [groupId]: ids }; saveOrder(pKey, next); return next; });
  };
  const recordRecent = (id: string) => {
    setRecentsState((r) => { const next = [id].concat(r.filter((x) => x !== id)).slice(0, RECENTS_MAX); saveRecents(pKey, next); return next; });
  };
  return { collapsed, setCollapsed, order, reorderGroup, recents, recordRecent };
}

/* Platform-detected palette shortcut. userAgentData is the modern signal;
 * navigator.platform is the fallback for browsers that don't ship it. */
const IS_MAC = (() => {
  try {
    const uad = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
    const p = (uad && uad.platform) || navigator.platform || "";
    return /mac/i.test(p);
  } catch { return false; }
})();
const SHORTCUT_LABEL = IS_MAC ? "⇧⌘K" : "Ctrl ⇧ K";

/** The keyboard hint that rides the search bar and the All-features entry. */
function ShortcutPill({ dim }: { dim?: boolean }) {
  return (
    <span aria-hidden="true" style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", height: 20, padding: "0 7px", borderRadius: 5, background: dim ? "transparent" : T.white, boxShadow: `0 0 0 1px ${T.border}`, color: T.n3, fontSize: 10.5, fontWeight: 600, letterSpacing: 0.2, fontFamily: T.font, whiteSpace: "nowrap" }}>{SHORTCUT_LABEL}</span>
  );
}

/** Panel/chevron toggle — collapse or expand the rail. */
const PanelToggleGlyph = ({ collapsed }: { collapsed: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="3.5" width="15" height="13" rx="2.5" />
    <path d="M8 3.5v13" />
    <path d={collapsed ? "M11.4 7.6 13.8 10l-2.4 2.4" : "M14.6 7.6 12.2 10l2.4 2.4"} />
  </svg>
);

/** Apps-grid (⊞) — the All-features launcher's icon. This was briefly a
 *  hand-drawn solid 18px mark, which broke the rail's icon language: every
 *  other icon in that column is jrni-ui's `Icon` set at 16px, fill="none",
 *  stroke-width 1.75, round caps, so the launcher read heavier and darker than
 *  the nav items above it (design-consistency gate: no hand-rolled twins of a
 *  jrni-ui component). The design system already ships a grid icon — this file
 *  uses it for Journey and Resources — so the launcher uses that same one. */
/*  (The four-box grid glyph that used to live here was removed 2026-08-07 with
 *  its only call site — the launcher now carries the JRNI mark. The lesson it
 *  documented still applies to this file: `Icon` members are glyph FACTORIES,
 *  so call them — `Icon.grid({ size })` — never alias one to a capitalised
 *  name and mount it as an element, which throws the minified React #130.) */

/** Six-dot grip — the drag affordance on reorderable launcher rows. */
const GripGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><circle cx="4" cy="2.5" r="1.1" /><circle cx="8" cy="2.5" r="1.1" /><circle cx="4" cy="6" r="1.1" /><circle cx="8" cy="6" r="1.1" /><circle cx="4" cy="9.5" r="1.1" /><circle cx="8" cy="9.5" r="1.1" /></svg>
);

/** Served pin state (REQ-131 — "add them to storage, fold them in"): boot from
 * the local cache for instant paint, then adopt the engine-served pins
 * (seed-world nav-pins.json + the user's persisted edits); writes go
 * optimistic-local + setNavPins. A tenant the engine doesn't know keeps
 * local/defaults — the op falls through and the catch stands the cache. */
/* Introduced pins (2026-10-02). A rail item newly added to a profile's defaultPins
 * never reached anyone who had already saved a pin set — saved pins win, by design.
 * An id listed here is offered ONCE per pin scope: prepended if the profile serves it
 * as a default and the saved set lacks it, then recorded so an unpin sticks. */
const INTRODUCED_PINS = ["ev-myday", "ev-mycal"];
function introKey(pKey: string): string { return `cdNavPinsIntro:${prefScope(pKey)}`; }
function useNavPins(pKey: string, items: RailItem[], servedDefaults: string[]) {
  const [userPins, setUserPins] = useState<string[] | null>(() => loadPins(pKey));
  useEffect(() => {
    if (!userPins) return;
    let seen: string[] = [];
    try { seen = JSON.parse(localStorage.getItem(introKey(pKey)) || "[]"); } catch { seen = []; }
    const add = INTRODUCED_PINS.filter((id) => !seen.includes(id) && servedDefaults.includes(id) && items.some((i) => i.id === id) && !userPins.includes(id));
    const mark = INTRODUCED_PINS.filter((id) => !seen.includes(id) && servedDefaults.includes(id));
    if (mark.length) { try { localStorage.setItem(introKey(pKey), JSON.stringify([...seen, ...mark])); } catch { /* private mode */ } }
    if (!add.length) return;
    /* ev-mycal sits right after ev-myday when the saved set already has it (2026-10-05). */
    const next = add.reduce((acc, id) => { const at = id === "ev-mycal" ? acc.indexOf("ev-myday") : -1; return at >= 0 ? [...acc.slice(0, at + 1), id, ...acc.slice(at + 1)] : [id, ...acc]; }, [...userPins]);
    setUserPins(next); savePins(pKey, next); setNavPins(next).catch(() => { /* local cache stands */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pKey, userPins, servedDefaults.join(","), items.length]);
  useEffect(() => {
    setUserPins(loadPins(pKey));
    let live = true;
    getNavPins().then((r) => {
      if (live && r && Array.isArray(r.pins)) { setUserPins(r.pins); savePins(pKey, r.pins); }
    }).catch(() => { /* undocumented tenant / offline — the local cache stands */ });
    return () => { live = false; };
  }, [pKey]);
  const pinIds = (userPins ?? servedDefaults).filter((id) => items.some((i) => i.id === id));
  const setPins = (ids: string[]) => { setUserPins(ids); savePins(pKey, ids); setNavPins(ids).catch(() => { /* keep local */ }); };
  const togglePin = (id: string) => setPins(pinIds.includes(id) ? pinIds.filter((p) => p !== id) : pinIds.concat(id));
  return { pinIds, setPins, togglePin };
}

const PinGlyph = ({ on, size = 15 }: { on: boolean; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={on ? T.p1 : "none"} stroke={on ? T.p1 : T.n4} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 4h6l-1 7 3 3H7l3-3-1-7ZM12 14v6" /></svg>
);

/** The JRNI mark (app/public/jrni-mark-user.png) — identity of the All button,
 * same asset-base mechanism as the topbar's JRNILogo. */
function JrniMark({ size = 24 }: { size?: number }) {
  const t = useT();
  const [broken, setBroken] = useState(false);
  const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
  if (broken) return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: Math.round(size / 4), background: T.brandFill, color: T.onColor, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.5), fontWeight: 800, fontFamily: T.font }}>{t("shell.chromeRail.text13", "j")}</span>;
  return <img src={`${base}/jrni-mark-user.png`} alt="" onError={() => setBroken(true)} style={{ width: size, height: size, objectFit: "contain", display: "block" }} />;
}

/* Launcher section colors — the Side Rail exploration's palette (operator-
 * reviewed doc) mapped onto GM routes; chips read the destination's process.
 * Ratification candidates for docs/design-baseline.md when the port lands. */ // ui-config
const CHIP_SECTION_COLORS: [RegExp, string][] = [
  [/^(dashboard|calendar|ops\.today|eventday)/, "#4F00F2"],
  [/^(appointments|ops\.)/, "#0AA396"],
  [/^(events|ticket-types|guests|customers|surveys|questions|vip)/, "#D61A55"],
  [/^queues/, "#B98F04"],
  [/^workforce/, "#3B3A9F"],
  [/^(campaigns|messaging|journeys|promos|landing|hub\.)/, "#8B2FD6"],
  [/^(products|orders)/, "#2E5CE6"],
  [/^(locations|staff|resources|approvals|reports|branding)/, "#5A6478"],
  [/^settings/, "#697180"],
  [/^(account|notifications|help)/, "#B92B8B"],
  [/^storeops/, "#B08D57"],
];
function chipColorFor(id: string): string {
  const route = RAIL_ROUTES[id] || id;
  for (const [re, c] of CHIP_SECTION_COLORS) if (re.test(route)) return c;
  return "#5A6478";
}

const CAPS_LABEL = { fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase" as const, color: T.soft };

/* LauncherTile / LauncherRow live at MODULE scope on purpose (2026-08-07 fix).
 * They used to be declared inside NavLauncher's body, which minted a NEW
 * component type on every render — React then unmounted and remounted the whole
 * pinned grid for any state change and DESTROYED the focused tile, so keyboard
 * carry lost focus to <body> after a single arrow press. Hoisted, the tiles are
 * updated in place (stable keys), focus survives natively, and the refocus
 * effect in NavLauncher is only a backstop. */
function LauncherTile({ i, idx, total, hot, mobile, dragging, carrying, hovered, dropBefore, dropAfter, t, onGo, onTogglePin, onHover, onDragStartTile, onDragEndTile, onDragOverTile, onDropTile, onArrow, onDropCarry, onBlurTile }: {
  i: RailItem; idx: number; total: number; hot?: boolean; mobile: boolean; dragging: boolean; carrying: boolean; hovered: boolean; dropBefore: boolean; dropAfter: boolean;
  t: (k: string, f?: string) => string; onGo: (i: RailItem) => void; onTogglePin: (id: string) => void; onHover: (k: string | null) => void;
  onDragStartTile: (id: string) => void; onDragEndTile: () => void; onDragOverTile: (at: number) => void; onDropTile: () => void;
  onArrow: (delta: number) => void; onDropCarry: () => void; onBlurTile: (id: string) => void;
}) {
  /* Real DOM focus. The tile sets outline:none, and the only ring it drew was
   * keyed to `hot` — the LAUNCHER PANEL's ↑↓ cursor — so a plain Tab onto the
   * tile landed with NO visible focus at all. Tracked here so the same 1px
   * utility-focus ring the rest of the system uses paints on Tab too. */
  const [focused, setFocused] = useState(false);
  const ring = `0 0 0 1px ${T.focus}`;
  return (
    <div role="button" tabIndex={0} data-pin-tile={i.id}
      draggable={!mobile}
      onDragStart={(e) => { onDragStartTile(i.id); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", i.id); } catch { /* older browsers */ } }}
      onDragEnd={onDragEndTile}
      onDragOver={(e) => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); onDragOverTile(e.clientX - r.left > r.width / 2 ? idx + 1 : idx); }}
      onDrop={(e) => { e.preventDefault(); onDropTile(); }}
      onMouseEnter={(e) => { onHover("p-" + i.id); if (hot) return; if (mobile) { e.currentTarget.style.boxShadow = `0 0 0 1px ${T.p1}`; } else { e.currentTarget.style.background = T.p4; } }}
      onMouseLeave={(e) => { onHover(null); if (hot) return; if (mobile) { e.currentTarget.style.boxShadow = `0 0 0 1px ${T.border}`; } else { e.currentTarget.style.background = "transparent"; } }}
      onClick={() => onGo(i)}
      onKeyDown={(e) => {
        if (!mobile && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
          // Arrow keys carry the tile; stop the panel's ↑↓ highlight handler
          // from also acting on this press.
          e.preventDefault(); e.stopPropagation();
          onArrow(e.key === "ArrowRight" ? 1 : -1);
        } else if (e.key === "Enter") {
          e.preventDefault(); e.stopPropagation();
          if (carrying) { onDropCarry(); return; } // drop, don't navigate
          onGo(i);
        }
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); onBlurTile(i.id); }}
      aria-label={i18nT("chrome.launcher.pinnedAria", "{label} — pinned {position}/{total}. Arrow keys reorder, Enter opens.", { label: i.label, position: idx + 1, total })}
      style={{ position: "relative", padding: mobile ? "12px 4px 10px" : "12px 6px 10px", borderRadius: mobile ? 10 : 8, background: mobile ? T.white : hot || carrying ? T.p4 : "transparent", boxShadow: carrying ? `${ring}, ${T.shadowMd}` : dragging ? T.shadowLg : hot || focused ? ring : mobile ? `0 0 0 1px ${T.border}` : "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 7, cursor: "pointer", outline: "none", opacity: dragging ? 0.5 : 1, transform: dragging || carrying ? "scale(1.03)" : "none", transition: "transform 120ms, box-shadow 120ms",
        borderLeft: dropBefore ? `2px solid ${T.p1}` : "2px solid transparent", borderRight: dropAfter ? `2px solid ${T.p1}` : "2px solid transparent" }}>
      <span style={{ width: mobile ? 34 : 32, height: mobile ? 34 : 32, borderRadius: 8, background: chipColorFor(i.id), color: T.onColor, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><span style={{ display: "inline-flex", transform: "scale(0.85)" }}>{i.svg}</span></span>
      <span style={{ fontSize: mobile ? 10.5 : 11.5, fontWeight: 500, color: T.n1, textAlign: "center", lineHeight: 1.2, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.label}</span>
      {!mobile ? (
        <span aria-label={i18nT("chrome.launcher.dragToReorderLabel", "Drag to reorder: {label}", { label: i.label })} title={t("chrome.launcher.dragToReorder", "Drag to reorder")} role="img"
          style={{ position: "absolute", top: 6, left: 6, color: T.n4, display: "inline-flex", cursor: "grab", opacity: hovered || dragging || carrying ? 1 : 0, transition: "opacity 100ms", pointerEvents: "none" }}><GripGlyph /></span>
      ) : null}
      {/* ui-raw: borderless icon affordance. IconButton deliberately always draws a 1px
          border (so its CSS state layer can apply) and Button's iconOnly is a filled/bordered
          control square. A ghost (borderless) icon-button variant is requested. */}
      {/* ui-raw: sub-32 borderless icon control — IconBtn always draws a border + chrome shadow (capability request 1) */}
      <button aria-label={t("chrome.launcher.unpin", "Unpin from rail")} title={t("chrome.launcher.unpin", "Unpin from rail")} onClick={(e) => { e.stopPropagation(); onTogglePin(i.id); }} style={{ position: "absolute", top: 3, right: 4, width: 30, height: 30, borderRadius: 7, border: "none", background: "transparent", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
        <PinGlyph on />
      </button>
    </div>
  );
}

function LauncherRow({ i, rowKey, hot, hovered, on, pinned, mobile, t, onGo, onTogglePin, onHover }: {
  i: RailItem; rowKey: string; hot?: boolean; hovered: boolean; on: boolean; pinned: boolean; mobile: boolean;
  t: (k: string, f?: string) => string; onGo: (i: RailItem) => void; onTogglePin: (id: string) => void; onHover: (k: string | null) => void;
}) {
  /* See LauncherTile: `hot` is the panel cursor, not DOM focus. */
  const [focused, setFocused] = useState(false);
  return (
    <div
      role="button" tabIndex={0} onClick={() => onGo(i)} onKeyDown={(e) => { if (e.key === "Enter") onGo(i); }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      onMouseEnter={() => onHover(rowKey)} onMouseLeave={() => onHover(null)}
      style={{ position: "relative", display: "flex", alignItems: "center", gap: mobile ? 11 : 9, height: mobile ? 48 : 34, padding: mobile ? "0 12px" : "0 8px", borderRadius: mobile ? 0 : 6, cursor: "pointer", outline: "none",
        background: on ? T.p3 : hot ? T.p4 : hovered ? T.canvas : "transparent",
        boxShadow: hot || focused ? `inset 0 0 0 1px ${T.focus}` : "none" }}>
      <span style={{ width: mobile ? 28 : 24, height: mobile ? 28 : 24, borderRadius: mobile ? 7 : 6, background: chipColorFor(i.id), color: T.onColor, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><span style={{ display: "inline-flex", transform: mobile ? "scale(0.8)" : "scale(0.7)" }}>{i.svg}</span></span>
      <span style={{ flex: 1, minWidth: 0, fontSize: mobile ? 14 : 13, fontWeight: on ? 700 : 500, color: on ? T.p1 : T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{i.label}</span>
      {mobile && i.badge ? <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: T.brandFill, color: T.onColor, fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 5px", flexShrink: 0 }}>{i.badge}</span> : null}
      {/* ui-raw: sub-32 borderless icon control — IconBtn always draws a 1px border + chrome
          shadow, and at size 34 it filled the whole 34px row, so the pin read as a second
          bordered box crowding the row edge (operator screenshot 2026-09-16). A ghost
          (borderless) icon-button variant is still the requested capability. */}
      <button aria-label={pinned ? t("chrome.launcher.unpin", "Unpin from rail") : t("chrome.launcher.pin", "Pin to rail")} title={pinned ? t("chrome.launcher.unpin", "Unpin from rail") : t("chrome.launcher.pin", "Pin to rail")} onClick={(e) => { e.stopPropagation(); onTogglePin(i.id); }}
        style={{ flexShrink: 0, width: mobile ? 32 : 26, height: mobile ? 32 : 26, borderRadius: 6, border: "none", background: "transparent", color: pinned ? T.p1 : T.n3, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, opacity: pinned || hovered || hot || focused || mobile ? 1 : 0, transition: "opacity 100ms" }}>
        <PinGlyph on={pinned} />
      </button>
    </div>
  );
}

/** The "All features" launcher — sanctioned CHROME (ruling R2), not a Drawer
 * or Modal. Faithful port of the exploration's Launchpad-style overlay:
 * desktop = 720px panel below the topbar (search pill, 6-col pinned tile grid,
 * two-column colored-chip sections, user footer); mobile = full-screen sheet
 * (4-col tiles, white card groups). No hubs, no recents (rulings D3/D4). */
function NavLauncher({ items, pins, recents, isActive, onTogglePin, onReorderPins, onGo, onClose, mobile }: { items: RailItem[]; pins: string[]; recents: string[]; isActive: (id: string) => boolean; onTogglePin: (id: string) => void; onReorderPins: (ids: string[]) => void; onGo: (i: RailItem) => void; onClose: () => void; mobile?: boolean }) {
  const t = useT();
  const nav = useNav();
  const u = useUser();
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /* Keyboard highlight across the whole visible list (↑↓ + Enter). `cursor` is
   * an index into `flat`, built below in the SAME order the panel paints. */
  const [cursor, setCursor] = useState(-1);
  const [hoverRow, setHoverRow] = useState<string | null>(null);
  /* PINNED-only drag state (2026-08-07 revision). The WORKSPACE /
   * ADMINISTRATION / RECENT lists are deliberately NOT reorderable: the pinned
   * grid IS the rail sequence, so it is the only order the user authors. */
  const [dragPin, setDragPin] = useState<string | null>(null);
  const [pinDropAt, setPinDropAt] = useState<number | null>(null);
  /* Keyboard reordering: once ←/→ moves a focused tile it enters "carry" mode
   * (Enter drops instead of opening), so Enter keeps its plain meaning until
   * the user has actually started moving something. */
  const [kbCarry, setKbCarry] = useState<string | null>(null);
  /* Esc handler reads the latest onClose through a ref so its effect can have
   * empty deps — see the autofocus note below. */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCloseRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); };
  }, []);
  /* Autofocus the search box ONCE, on mount. This used to sit in the effect
   * above with [onClose, mobile] deps — but `onClose` is re-created on every
   * IconRail render, so the effect re-ran on each render and re-scheduled
   * "focus the input" ~60ms later. Reordering a pinned tile re-renders the
   * rail, so every arrow press stole focus back from the carried tile. */
  useEffect(() => {
    if (mobile) return;
    const id = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, []);
  useEffect(() => { setCursor(-1); }, [q]);
  const query = q.trim().toLowerCase();
  const pinnedItems: RailItem[] = [];
  pins.forEach((id) => { const f = items.find((i) => i.id === id); if (f) pinnedItems.push(f); });
  /* RECENT (2026-08-07) — the last five features opened, most-recent first,
   * filtered through the live catalog so a feature the role lost access to (or
   * a stale id from an older build) simply drops out. Hidden entirely when the
   * history is empty, and suppressed while searching. */
  const recentItems: RailItem[] = [];
  recents.forEach((id) => { const f = items.find((i) => i.id === id); if (f) recentItems.push(f); });
  const groups: { id: string; label: string; items: RailItem[] }[] = [];
  items.forEach((i) => {
    /* Matched through nav-search (2026-09-11): rendered label + catalog id +
       ROUTE + aliases, not the label alone. On a university tenant this row is
       labelled "Campuses" (the served tenant noun), so "location" and "store"
       used to find nothing though the Locations CRUD was one click away. */
    if (query && !matchesNavQuery(i, query, railRoute(i.id))) return;
    const g = i.group || "main";
    let grp = groups.find((x) => x.id === g);
    if (!grp) {
      const label = railGroupLabel(t, g) || (g === "profile-top" ? t("chrome.launcher.workspace", "Workspace") : g === "profile-bottom" ? t("chrome.launcher.administration", "Administration") : t("chrome.launcher.all", "All"));
      grp = { id: g, label, items: [] };
      groups.push(grp);
    }
    grp.items.push(i);
  });
  // Groups keep their catalog order — fixed, pin/unpin only.
  const showRecent = !query && recentItems.length > 0;
  // Flat keyboard order, mirroring the paint order exactly.
  const flat: { key: string; item: RailItem }[] = [];
  if (!query) pinnedItems.forEach((i) => flat.push({ key: "p-" + i.id, item: i }));
  if (showRecent) recentItems.forEach((i) => flat.push({ key: "r-" + i.id, item: i }));
  groups.forEach((g) => g.items.forEach((i) => flat.push({ key: g.id + "-" + i.id, item: i })));
  const hotKey = cursor >= 0 && flat[cursor] ? flat[cursor].key : null;
  /* Live pinned order for the reorder handlers (see nudgePin). */
  const pinOrderRef = useRef<string[]>([]);
  pinOrderRef.current = pinnedItems.map((i) => i.id);
  const onPanelKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!flat.length) return;
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setCursor((c) => { const n = c + step; return n < 0 ? flat.length - 1 : n >= flat.length ? 0 : n; });
    } else if (e.key === "Enter") {
      if (cursor >= 0 && flat[cursor]) { e.preventDefault(); onGo(flat[cursor].item); }
    } else if (e.key === "Tab") {
      // Focus trap: Tab cycles within the panel, never back out to the page.
      const root = panelRef.current;
      if (!root) return;
      const f = Array.from(root.querySelectorAll<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])')).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };
  /** Move a pinned id to an insertion index and persist (setPins writes
   *  localStorage + setNavPins). The rail reads the same array, so the grid
   *  order IS the rail order with no second source of truth. */
  const movePin = (fromId: string, toIdx: number) => {
    const ids = pinOrderRef.current.slice();
    const from = ids.indexOf(fromId);
    if (from < 0) return;
    const dest = toIdx > from ? toIdx - 1 : toIdx;
    if (dest === from) return;
    ids.splice(from, 1);
    ids.splice(dest, 0, fromId);
    onReorderPins(ids);
  };
  const nudgePin = (id: string, delta: number) => {
    // Read the LIVE order, not this render's closure: two arrow presses inside
    // one commit would otherwise both compute from the stale array and the
    // second would be swallowed (verifier saw 3 presses move 2 slots).
    const ids = pinOrderRef.current.slice();
    const from = ids.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    ids.splice(from, 1);
    ids.splice(to, 0, id);
    onReorderPins(ids);
    // Keep the live order in step immediately, so a second press within the
    // same commit continues from the new positions.
    pinOrderRef.current = ids;
    setKbCarry(id);
    // Focus restoration is a commit-time effect (below), not an rAF here: the
    // reordered grid doesn't exist yet at this point, and the launcher's
    // mount-time input focus can win the race.
  };
  /* Keep the carried tile focused across the reorder commit, so a second arrow
   * press keeps moving the SAME tile instead of landing in the search box. */
  useEffect(() => {
    if (!kbCarry) return;
    try {
      const el = panelRef.current?.querySelector<HTMLElement>(`[data-pin-tile="${kbCarry}"]`);
      if (el && document.activeElement !== el) el.focus();
    } catch { /* detached */ }
  }, [kbCarry, pins]);
  const capsLabel = CAPS_LABEL;
  /* Carry mode ends only when focus genuinely settles OUTSIDE the pinned grid.
   * A bare <body> activeElement right after a commit is transient (React may
   * still be re-parenting), and clearing on it is what previously stranded the
   * carry — leave it to the refocus effect. */
  const handleTileBlur = (id: string) => {
    setTimeout(() => {
      const a = document.activeElement as HTMLElement | null;
      if (!a || a === document.body) return;
      if (a.closest && a.closest("[data-pin-grid]")) return;
      setKbCarry((c) => (c === id ? null : c));
    }, 0);
  };
  const renderTile = (i: RailItem, idx: number) => (
    <LauncherTile key={"p-" + i.id} i={i} idx={idx} total={pinnedItems.length} mobile={!!mobile} t={t}
      hot={hotKey === "p-" + i.id} dragging={dragPin === i.id} carrying={kbCarry === i.id} hovered={hoverRow === "p-" + i.id}
      dropBefore={pinDropAt === idx} dropAfter={idx === pinnedItems.length - 1 && pinDropAt === idx + 1}
      onGo={onGo} onTogglePin={onTogglePin} onHover={(k) => setHoverRow(k === null ? null : k)}
      onDragStartTile={setDragPin} onDragEndTile={() => { setDragPin(null); setPinDropAt(null); }}
      onDragOverTile={(at) => { if (dragPin) setPinDropAt(at); }}
      onDropTile={() => { if (dragPin && pinDropAt !== null) movePin(dragPin, pinDropAt); setDragPin(null); setPinDropAt(null); }}
      onArrow={(d) => nudgePin(i.id, d)} onDropCarry={() => setKbCarry(null)} onBlurTile={handleTileBlur} />
  );
  const renderRow = (i: RailItem, kk: string) => (
    <LauncherRow key={kk} rowKey={kk} i={i} mobile={!!mobile} t={t} hot={hotKey === kk} hovered={hoverRow === kk}
      on={isActive(i.id)} pinned={pins.includes(i.id)} onGo={onGo} onTogglePin={onTogglePin} onHover={setHoverRow} />
  );
  /* A plain render function, not a component: an inner component type would
   * remount its rows on every render (the same defect fixed above). */
  const renderRecent = (pad: string) => (
    <>
      <div style={{ ...capsLabel, padding: pad }}>{t("chrome.launcher.recent", "Recent")} <span style={{ fontWeight: 500, color: T.n3, textTransform: "none", letterSpacing: 0 }}>{t("chrome.launcher.recentSub", "— where you were last")}</span></div>
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: mobile ? 0 : "2px 14px", ...(mobile ? { background: T.white, borderRadius: 10, boxShadow: `0 0 0 1px ${T.border}`, overflow: "hidden" } : null) }}>
        {recentItems.map((i) => renderRow(i, "r-" + i.id))}
      </div>
    </>
  );
  const initials = (u.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (!mobile) {
    return createPortal(
      // ds-pair-ok(D4-OVERLAY): APP CHROME, not a dialog — this is the All-features LAUNCHER (a command palette), the desktop twin of the mobile branch below that this rule already classifies as its own false-positive class. Three things here are not expressible through either shell primitive: it is TOP-ANCHORED and offset to clear the topbar (top:56 + alignItems:flex-start + a 64px top inset) where Modal centres in the viewport; it paints a backdrop-filter blur over the app rather than an opaque card; and it runs its own roving arrow-key navigation over the tile grid via onPanelKey/panelRef, which is the palette interaction, not Tab containment. MISSING DS CAPABILITY — a CommandPalette primitive (or an Overlay anchor="top" variant with a blur scrim and a caller-owned key handler) is the prop/variant that would let this drain; it would also absorb the mobile full-screen branch and chrome-topbar search.
      <div onClick={onClose} role="dialog" aria-modal="true" aria-label={t("chrome.launcher.allFeatures", "All features")} style={{ position: "fixed", left: 0, right: 0, bottom: 0, top: 56, zIndex: 240, background: alpha(T.chromeInk, 45), backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "64px 20px 20px" }}>
        <div ref={panelRef} onKeyDown={onPanelKey} onClick={(e) => e.stopPropagation()} style={{ width: 720, maxWidth: "94vw", maxHeight: "78vh", background: T.white, borderRadius: 12, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: T.font }}>
          <div style={{ flexShrink: 0, padding: "16px 18px 12px" }}>
            <div data-jrni-component="field-wrap" style={{ height: 40, borderRadius: 20, background: T.canvas, display: "flex", alignItems: "center", gap: 11, padding: "0 15px" }}>
              <span style={{ color: T.n2, display: "inline-flex", flexShrink: 0 }}><Icon.search size={14} /></span>
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("chrome.launcher.searchAllFeatures", "Search all features")} style={{ flex: 1, minWidth: 0, fontSize: 14, color: T.n1, border: "none", outline: "none", background: "transparent", fontFamily: T.font }} />
              <ShortcutPill />
              <span style={{ fontSize: 11, color: T.n3, flexShrink: 0, whiteSpace: "nowrap" }}>{t("chrome.launcher.escToClose", "Esc\u00A0to\u00A0close")}</span>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 18px 18px" }}>
            {!query ? (<>
              <div style={{ ...capsLabel, padding: "6px 0" }}>{t("chrome.launcher.pinned", "Pinned")} <span style={{ fontWeight: 500, color: T.n3, textTransform: "none", letterSpacing: 0 }}>{t("chrome.launcher.showsOnRail", "— shows on the rail")}</span></div>
              {pinnedItems.length ? (
                <div data-pin-grid style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>{pinnedItems.map(renderTile)}</div>
              ) : (
                <div style={{ padding: "4px 0 8px", fontSize: 12.5, color: T.soft }}>{t("chrome.launcher.nothingPinned", "Nothing pinned — use the pin on any row below.")}</div>
              )}
            </>) : null}
            {showRecent ? renderRecent("14px 0 5px") : null}
            {groups.map((g) => (
              <div key={g.id}>
                <div style={{ ...capsLabel, padding: "14px 0 5px" }}>{g.label}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 14px" }}>{g.items.map((i) => renderRow(i, g.id + "-" + i.id))}</div>
              </div>
            ))}
            {query && !groups.length ? <div style={{ padding: 28, textAlign: "center", fontSize: 13, color: T.soft }}>{t("chrome.launcher.noMatches", "No features match")} “{q}”</div> : null}
          </div>
          <div style={{ flexShrink: 0, borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, padding: "10px 16px" }}>
            <span style={{ position: "relative", overflow: "hidden", width: 32, height: 32, borderRadius: 8, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {initials}
              {/* Their own face over the initials — `onError` uncovers the
                  initials again when the persona has no headshot on file. */}
              {portraitSrc() ? <img src={portraitSrc()} alt="" aria-hidden onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} /> : null}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name}</div>
              <div style={{ fontSize: 11, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.role}</div>
            </div>
            <Btn variant="ghost" size="sm" onClick={async () => { await signOut(); nav.reset("signin"); }}>{t("shell.appLauncher.signOut", "Sign out")}</Btn>
          </div>
        </div>
      </div>,
      document.body,
    );
  }
  return createPortal(
    <div ref={panelRef} onKeyDown={onPanelKey} role="dialog" aria-modal="true" aria-label={t("chrome.launcher.allFeatures", "All features")} style={{ position: "fixed", inset: 0, zIndex: 240, background: T.canvas, display: "flex", flexDirection: "column", fontFamily: T.font }}>
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8, padding: "10px 10px 6px" }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 700, color: T.n1, paddingLeft: 4 }}>{t("chrome.launcher.allFeatures", "All features")}</span>
        {/* ui-raw: borderless icon affordance. IconButton deliberately always draws a 1px
            border (so its CSS state layer can apply) and Button's iconOnly is a filled/bordered
            control square. A ghost (borderless) icon-button variant is requested. */}
        {/* ui-raw: 44px touch-target close on the launcher overlay — IconBtn is 32/40/48 and borderless is capability 1 */}
        <button aria-label={t("common.actions.close", "Close")} onClick={onClose} style={{ width: 44, height: 44, borderRadius: 10, border: "none", background: "transparent", color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.x size={16} /></button>
      </div>
      <div data-jrni-component="field-wrap" style={{ flexShrink: 0, margin: "0 12px 10px", height: 40, borderRadius: 20, background: T.white, boxShadow: `0 0 0 1px ${T.border}`, display: "flex", alignItems: "center", gap: 9, padding: "0 14px" }}>
        <span style={{ color: T.n2, display: "inline-flex", flexShrink: 0 }}><Icon.search size={13} /></span>
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("chrome.launcher.searchAllFeatures", "Search all features")} style={{ flex: 1, minWidth: 0, fontSize: 14, color: T.n1, border: "none", outline: "none", background: "transparent", fontFamily: T.font }} />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 12px 16px" }}>
        {!query ? (<>
          <div style={{ ...capsLabel, padding: "6px 4px" }}>{t("chrome.launcher.pinned", "Pinned")} <span style={{ fontWeight: 500, color: T.n3, textTransform: "none", letterSpacing: 0 }}>{t("chrome.launcher.firstFourRideBar", "— first four ride the bottom bar")}</span></div>
          {pinnedItems.length ? (
            <div data-pin-grid style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>{pinnedItems.map(renderTile)}</div>
          ) : (
            <div style={{ padding: "4px 4px 8px", fontSize: 12.5, color: T.soft }}>{t("chrome.launcher.nothingPinned", "Nothing pinned — use the pin on any row below.")}</div>
          )}
        </>) : null}
        {showRecent ? renderRecent("14px 4px 5px") : null}
        {groups.map((g) => (
          <div key={g.id}>
            <div style={{ ...capsLabel, padding: "14px 4px 5px" }}>{g.label}</div>
            <div style={{ background: T.white, borderRadius: 10, boxShadow: `0 0 0 1px ${T.border}`, overflow: "hidden" }}>{g.items.map((i) => renderRow(i, g.id + "-" + i.id))}</div>
          </div>
        ))}
        {query && !groups.length ? <div style={{ padding: 28, textAlign: "center", fontSize: 13, color: T.soft }}>{t("chrome.launcher.noMatches", "No features match")} “{q}”</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/** UIR-47 (operator report, 2026-07-25): the rail highlight compared the
 * screen's literal `rail` id against the item id — but nav-profile items and
 * default items use DIFFERENT ids for the same destination (profile "services"
 * vs screen rail="cs-services"), so clicking e.g. Booking experience or
 * Services navigated correctly and then lost its highlight. Equivalence now
 * follows the ROUTE: same resolved route, or the current route inside the
 * item's route section, counts as active.
 *
 * ⭐ HARD RULE (2026-07-29, operator): EXACTLY ONE rail item may look selected.
 * The section-prefix fallback below used to run even when the screen declared
 * its own rail id, so `queues.console` lit BOTH Concierge (`q-console`, declared)
 * and Queues (`q-board` → `queues`, prefix match). Two rules keep it single:
 *   1. A screen that declares `rail="<id>"` OWNS the highlight — only that id
 *      (or an alias resolving to the identical route) may match; no prefix
 *      fallback runs at all.
 *   2. With no declared id, the route match must be the LONGEST matching item
 *      route, so a child route never also lights its parent section.
 * Any new rail item or screen has to keep this invariant. */
function railHit(active: string | boolean | undefined, id: string, route: string): boolean {
  if (active === id) return true;
  const mine = RAIL_ROUTES[id];
  if (!mine) return false;
  if (typeof active === "string") return RAIL_ROUTES[active] === mine; // rule 1 — declared id is exclusive
  if (route !== mine && !route.startsWith(mine + ".")) return false;
  // rule 2 — only the most specific matching item wins
  let best = mine;
  for (const r of Object.values(RAIL_ROUTES)) {
    if ((route === r || route.startsWith(r + ".")) && r.length > best.length) best = r;
  }
  return best === mine;
}

/**
 * D4-OVERLAY drain. A centred 420px decision — the textbook Modal case, and the
 * one site in this file that genuinely IS a dialog (the launcher above is chrome).
 * `showCloseButton={false}` plus no `title` suppresses the header band entirely, so
 * the centre-aligned card content renders exactly as authored; `aria-label` keeps
 * the announced name the header would otherwise have supplied. The local Escape
 * listener is deleted — Modal owns Escape, backdrop dismiss, the focus trap and
 * focus restore — and the off-scale 14px radius becomes --jrni-radius-lg.
 */
function ComingSoonModal({ title, onClose }: { title: string; onClose: () => void; }) {
  const t = useT();
  return (
    <Modal open onClose={onClose} maxWidth={420} padded={false} showCloseButton={false}
      aria-label={i18nT("chrome.comingSoon.titleComingSoon.icu", "{value0} — coming soon", { value0: title })}>
      <div style={{ textAlign: "center", padding: "32px 28px" }}>
        <span style={{ width: 56, height: 56, borderRadius: 14, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><Icon.clock size={26} /></span>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, background: T.p4, color: T.p1, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>{t("chrome.comingSoon.comingSoon", "Coming soon")}</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.n1, margin: "0 0 8px" }}>{title}</h2>
        <p style={{ fontSize: 14, color: T.soft, lineHeight: 1.55, margin: "0 0 22px" }}><b style={{ color: T.n2, fontWeight: 600 }}>{title}</b> {t("chrome.comingSoon.isnTAvailableYetItSOnTheRoadmapWeLlLet", "isn't available yet — it's on the roadmap. We'll let you know the moment it's ready.")}</p>
        <Btn onClick={onClose}>{t("common.actions.close", "Close")}</Btn>
      </div>
    </Modal>
  );
}

/** THE nav catalog for the signed-in user — role-filtered, tenant-labelled,
 *  in catalog order. One source for the rail, the All-features launcher AND
 *  (2026-09-11) the topbar's global search, which until now carried its own
 *  hand-written list of eighteen destinations: Locations, Events, Queues,
 *  Staff, Journeys and Services were absent from it entirely, so the two
 *  searches disagreed about what the product contains. */
export function useRailCatalog(): { id: string; label: string; labelKey: string; route?: string; group?: string }[] {
  const u = useUser();
  const tenant = useTenant();
  const hidden = navHiddenForRole(u.role);
  const lbl = useTenantLabels();
  const t = useT();
  const served = new Set(useNavComposition()?.railItemIds ?? []);
  const profile = navProfileForUser(u, tenant?.slug ?? null);
  const items = profile
    ? profile.items.filter((i) => _railCan(i.needs)).map((i) => ({ id: i.id, label: railLabel(t, i.labelKey, i.label), labelKey: i.labelKey, group: i.bottom ? "profile-bottom" : "profile-top" }))
    : RAIL_ITEMS.filter((i) => _railCan(i.needs) && !hidden.has(i.id) && (!i.gated || served.has(i.id))).map((i) => ({ id: i.id, label: railLabel(t, i.labelKey, i.tenantLabelKey ? lbl(i.tenantLabelKey, i.label) : i.label), labelKey: i.labelKey, group: i.group }));
  return items.map((i) => ({ ...i, route: railRoute(i.id) }));
}

export function IconRail({ active }: { active?: string; }) {  const nav = useNav();
  const u = useUser();
  const tenant = useTenant();
  const hidden = navHiddenForRole(u.role);
  const lbl = useTenantLabels();
  const t = useT(); // shell.rail.* resolution (CD-RAIL-I18N-NOTE-2026-07-20)
  const served = new Set(useNavComposition()?.railItemIds ?? []); // §1.2.4 — server-enabled ids for `gated` rail items
  // Role nav-profile (bank six-persona port): when the signed-in
  // role has a declared rail organization, render it verbatim — order,
  // labels, badges, bottom section. The profile IS the menu authority for
  // that role (NAV_HIDDEN_BY_ROLE doesn't apply); caps still gate each item.
  const profile = navProfileForUser(u, tenant?.slug ?? null);
  const items: RailItem[] = profile
    ? profile.items.filter((i) => _railCan(i.needs)).map((i) => ({ id: i.id, label: railLabel(t, i.labelKey, i.label), labelKey: i.labelKey, svg: PROFILE_ICONS[i.icon] ?? <Icon.grid />, group: i.bottom ? "profile-bottom" : "profile-top", badge: i.badge }))
    : RAIL_ITEMS.filter((i) => _railCan(i.needs) && !hidden.has(i.id) && (!i.gated || served.has(i.id))).map((i) => ({ ...i, label: railLabel(t, i.labelKey, i.tenantLabelKey ? lbl(i.tenantLabelKey, i.label) : i.label) }));
  // ── Pin state — engine-served (REQ-131), local cache for instant paint ──
  const pKey = pinsKey(tenant?.slug ?? null, profile ? (profile.role || profile.landing) : null);
  const servedDefaults = defaultPinsFor(profile, items);
  const { pinIds, setPins, togglePin } = useNavPins(pKey, items, servedDefaults);
  // Collapsed/expanded + list order + recents (2026-08-07). All three are
  // browser-local preferences; pins keep riding the served ops above.
  const { collapsed, setCollapsed, recents, recordRecent } = useNavPrefs(pKey);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  /* Collapsed tooltips are PORTALLED and fixed-positioned: the rail is
   * overflow:hidden (the pin list scrolls), so an in-flow tooltip would clip. */
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);
  const [launcher, setLauncher] = useState(false);
  const allBtnRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const skipRestoreRef = useRef(false);
  /* `document.activeElement` is essentially never null — with nothing focused
    it is <body>, which is truthy and whose .focus() is a no-op. Capturing it
    dropped focus on the document when the palette was opened by chord from a
    cold page. Only a genuinely focusable element counts; otherwise the
    launcher button IS the return target. */
  const focusTarget = (): HTMLElement | null => {
    const ae = document.activeElement as HTMLElement | null;
    return ae && ae !== document.body && ae !== document.documentElement ? ae : allBtnRef.current;
  };
  // Ruling R2: the launcher rides the URL (?launcher=1) so hardware/browser
  // Back closes it — same contract as Drawer panel keys. Best-effort.
  const openLauncher = () => {
    returnFocusRef.current = focusTarget();
    setLauncher(true);
    try { const u = new URL(location.href); if (!u.searchParams.has("launcher")) { u.searchParams.set("launcher", "1"); history.pushState({ cdLauncher: 1 }, "", u.toString()); } } catch { /* URL ride is best-effort */ }
  };
  const closeLauncher = () => {
    setLauncher(false);
    // Focus returns to whatever opened it (the All button, or the page element
    // that had focus when ⌘K fired) — never dropped on <body>.
    const captured = returnFocusRef.current;
    const usable = captured && captured !== document.body && captured !== document.documentElement && captured.isConnected ? captured : allBtnRef.current;
    const back = skipRestoreRef.current ? null : usable;
    skipRestoreRef.current = false;
    if (back) setTimeout(() => { try { back.focus(); } catch { /* detached */ } }, 0);    try { if (history.state && (history.state as { cdLauncher?: number }).cdLauncher) { history.back(); } else { const u = new URL(location.href); if (u.searchParams.has("launcher")) { u.searchParams.delete("launcher"); history.replaceState(history.state, "", u.toString()); } } } catch { /* best-effort */ }
  };
  useEffect(() => {
    const onPop = () => { try { if (!new URL(location.href).searchParams.has("launcher")) setLauncher(false); } catch { /* noop */ } };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  /* ⇧⌘K / Ctrl+Shift+K opens the features launcher from anywhere. The PLAIN
   * ⌘K chord belongs to the topbar's global search (chrome-topbar.tsx) — both
   * were bound to it for one build, so both fired at once; the launcher took
   * the shifted chord (2026-08-07). */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setLauncher((open) => { if (open) return open; returnFocusRef.current = focusTarget(); try { const u = new URL(location.href); if (!u.searchParams.has("launcher")) { u.searchParams.set("launcher", "1"); history.pushState({ cdLauncher: 1 }, "", u.toString()); } } catch { /* best-effort */ } return true; });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const [comingSoon, setComingSoon] = useState<string | null>(null);

  // ── The pinned rail — the only rail since the 2026-08-06 cutover. Collapsed
  // (64px, icon-only) is the shipped default and the cutover look; expanded
  // (216px, icon + label) is the 2026-08-07 addition, persisted per browser. ──
  {
    const RAIL_W = collapsed ? 64 : 216;
    const pinnedItems: RailItem[] = [];
    pinIds.forEach((id) => { const f = items.find((i) => i.id === id); if (f) pinnedItems.push(f); });
    const go = (i: RailItem) => {
      if (i.comingSoon) { setComingSoon(i.label); return; }
      const r = railRoute(i.id);
      if (r) { recordRecent(i.id); nav.go(r); }
    };
    const goFromLauncher = (i: RailItem) => {
      if (i.comingSoon) { setComingSoon(i.label); return; }
      const r = railRoute(i.id);
      if (!r) return;
      recordRecent(i.id);
      nav.go(r);
      // Focus goes to the destination, not back to the rail: the route
      // re-render lands AFTER a 0ms restore and would blow it away, dropping
      // focus on <body>. #main-content is the page's own tabIndex={-1} target
      // (chrome.tsx), so Tab continues from the content the user just opened.
      returnFocusRef.current = null;
      skipRestoreRef.current = true;
      closeLauncher();
      requestAnimationFrame(() => { try { (document.getElementById("main-content") || allBtnRef.current)?.focus(); } catch { /* detached */ } });
    };
    const showTip = (label: string) => (e: React.SyntheticEvent<HTMLElement>) => {
      if (!collapsed) return;
      const r = e.currentTarget.getBoundingClientRect();
      setTip({ label, top: r.top + r.height / 2 });
    };
    const hideTip = () => setTip(null);
    return (
      <>
        <nav aria-label={t("shell.rail.primaryNavigation", "Primary navigation")} style={{ width: RAIL_W, flexShrink: 0, background: T.white, borderRight: `1px solid ${T.border}`, padding: "10px 0", display: "flex", flexDirection: "column", alignItems: collapsed ? "center" : "stretch", gap: 4, minHeight: 0, overflow: "hidden", transition: "width 160ms cubic-bezier(0.4,0,0.2,1)" }}>
          <div style={{ display: "flex", justifyContent: collapsed ? "center" : "flex-end", padding: collapsed ? 0 : "0 12px", flexShrink: 0 }}>
            {/* ui-raw: borderless icon affordance. IconButton deliberately always draws a 1px
                border (so its CSS state layer can apply) and Button's iconOnly is a filled/bordered
                control square. A ghost (borderless) icon-button variant is requested. */}
            {/* ui-raw: rail collapse/expand chip — SideNav owns its own collapse affordance (group 8) */}
            <button onClick={() => { setCollapsed(!collapsed); setTip(null); }} aria-label={collapsed ? t("chrome.rail.expand", "Expand navigation") : t("chrome.rail.collapse", "Collapse navigation")} aria-expanded={!collapsed} title={collapsed ? t("chrome.rail.expand", "Expand navigation") : t("chrome.rail.collapse", "Collapse navigation")}
              style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.n6)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <PanelToggleGlyph collapsed={collapsed} />
            </button>
          </div>
          {/* The rail's old "Search" row (JRNI mark + pill) was REMOVED
              2026-08-07 (operator): redundant with the topbar's global search.
              The features launcher gets its own dedicated entry at the BOTTOM
              of the rail instead — see the apps-grid button below the list. */}
          <ul style={{ listStyle: "none", margin: 0, padding: collapsed ? 0 : "0 10px",
            /* Collapsed, this list is its own scroller, and a 10px scrollbar was
               eating the right of its content box — so its centred 38px icons
               landed 5px left (cx 27) of the collapse toggle and the All-features
               button, which sit OUTSIDE the scroller on the rail's true centre
               (cx 32). Full width + a symmetric gutter puts every rail control on
               one line, scrollbar present or not (operator, 2026-08-07). */
            width: "100%", boxSizing: "border-box", scrollbarGutter: collapsed ? "stable both-edges" : "auto",
            display: "flex", flexDirection: "column", alignItems: collapsed ? "center" : "stretch", gap: 4, flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
            {pinnedItems.map((i, idx) => {
              const on = railHit(active, i.id, nav.route);
              const dragging = dragIdx === idx;
              return (
                <li key={i.id} draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => { e.preventDefault(); if (dragIdx === null || dragIdx === idx) return; const cur = pinIds.slice(); const m = cur.splice(dragIdx, 1)[0]; cur.splice(idx, 0, m); setPins(cur); setDragIdx(idx); }}
                  onDragEnd={() => setDragIdx(null)}
                  onMouseEnter={() => setHoverIdx(idx)} onMouseLeave={() => setHoverIdx((h) => (h === idx ? null : h))}
                  style={{ display: "flex", justifyContent: "center", opacity: dragging ? 0.5 : 1, boxShadow: dragging ? `0 0 0 2px ${T.p1}` : "none", borderRadius: 8 }}>
                  {/* ui-raw: rail navigation row — aria-current="page", brand active fill, drag grip and
                      badge. This is SideNav's job, not Button's; the rail should adopt jrni-ui SideNav in
                      its own pass rather than be dressed as a Button. */}
                  {/* ui-raw: rail navigation item — SideNav's pattern (group 8), not Button's */}
                  <button title={collapsed ? undefined : i.label} aria-label={i.label} aria-current={on ? "page" : undefined} onClick={() => go(i)}
                    onMouseEnter={(e) => { showTip(i.label)(e); if (!on) e.currentTarget.style.background = T.n6; }}
                    onMouseLeave={(e) => { hideTip(); e.currentTarget.style.background = on ? T.p3 : "transparent"; }}
                    onFocus={(e) => showTip(i.label)(e)} onBlur={hideTip}
                    style={{ position: "relative", width: collapsed ? 38 : "100%", height: 38, borderRadius: 8, border: "none", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 10, padding: collapsed ? 0 : "0 9px", cursor: "pointer", background: on ? T.p3 : "transparent", color: on ? T.p1 : T.n2, transition: "background 120ms, color 120ms", fontFamily: T.font }}>
                    <span style={{ display: "inline-flex", flexShrink: 0 }}>{i.svg}</span>
                    {!collapsed ? <span style={{ flex: 1, minWidth: 0, textAlign: "left", fontSize: 13, fontWeight: on ? 700 : 500, color: on ? T.p1 : T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{i.label}</span> : null}
                    {!collapsed ? <span aria-hidden="true" style={{ flexShrink: 0, color: T.n4, display: "inline-flex", opacity: hoverIdx === idx || dragging ? 1 : 0, transition: "opacity 100ms", cursor: "grab" }}><GripGlyph /></span> : null}
                    {i.badge ? <span aria-label={i18nT("chrome.tooltips.itemBadgePending.icu", "{value0} pending", { value0: i.badge })} style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: T.p1, border: `1.5px solid ${T.white}` }} /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {/* All-features launcher — pinned to the BOTTOM of the rail, separated
              from the nav list by a rule so it doesn't read as another
              destination. This is the palette's dedicated trigger (⇧⌘K /
              Ctrl+Shift+K still opens it from anywhere). */}
          <span aria-hidden="true" style={{ width: collapsed ? 32 : "auto", height: 1, background: T.border, margin: collapsed ? "6px 0" : "6px 14px", flexShrink: 0 }} />
          <div style={{ display: "flex", justifyContent: "center", padding: collapsed ? 0 : "0 10px", flexShrink: 0 }}>
            {/* ui-raw: rail navigation row — aria-current="page", brand active fill, drag grip and
                badge. This is SideNav's job, not Button's; the rail should adopt jrni-ui SideNav in
                its own pass rather than be dressed as a Button. */}
            {/* ui-raw: rail navigation item — SideNav's pattern (group 8), not Button's */}
            <button ref={allBtnRef} onClick={openLauncher} aria-label={t("chrome.launcher.allFeatures", "All features")} aria-haspopup="dialog" aria-expanded={launcher}
              title={collapsed ? undefined : t("chrome.launcher.allFeatures", "All features")}
              onMouseEnter={(e) => { showTip(t("chrome.launcher.allFeatures", "All features"))(e); if (!launcher) e.currentTarget.style.background = T.n6; }}
              onMouseLeave={(e) => { hideTip(); e.currentTarget.style.background = launcher ? T.p3 : "transparent"; }}
              onFocus={(e) => showTip(t("chrome.launcher.allFeatures", "All features"))(e)} onBlur={hideTip}
              style={{ width: collapsed ? 38 : "100%", height: 38, borderRadius: 8, border: "none", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 10, padding: collapsed ? 0 : "0 9px", cursor: "pointer", background: launcher ? T.p3 : "transparent", color: launcher ? T.p1 : T.n2, transition: "background 120ms, color 120ms", fontFamily: T.font }}>
              <span style={{ display: "inline-flex", flexShrink: 0 }}><JrniMark size={18} /></span>
              {!collapsed ? <span style={{ flex: 1, minWidth: 0, textAlign: "left", fontSize: 13, fontWeight: launcher ? 700 : 500, color: launcher ? T.p1 : T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t("chrome.launcher.allFeatures", "All features")}</span> : null}
            </button>
          </div>
        </nav>
        {tip && collapsed ? createPortal(
          <div role="tooltip" style={{ position: "fixed", left: 70, top: tip.top, transform: "translateY(-50%)", zIndex: 300, background: T.chromeInk, color: T.onColor, fontFamily: T.font, fontSize: 12, fontWeight: 500, padding: "5px 9px", borderRadius: 6, boxShadow: T.shadowMd, pointerEvents: "none", whiteSpace: "nowrap" }}>{tip.label}</div>,
          document.body,
        ) : null}
        {comingSoon ? <ComingSoonModal title={comingSoon} onClose={() => setComingSoon(null)} /> : null}
        {launcher ? <NavLauncher items={items} pins={pinIds} recents={recents} isActive={(id) => railHit(active, id, nav.route)} onTogglePin={togglePin} onReorderPins={setPins} onGo={goFromLauncher} onClose={closeLauncher} /> : null}
      </>
    );
  }
}

/** MobileBottomNav — the phone chrome (exploration Concept C): the first
 * four pins become a bottom tab bar; "All" (JRNI mark) opens the full-screen
 * launcher sheet. THE mobile navigation since the 2026-08-06 cutover — the
 * classic hamburger drawer (MobileRailMenu) was removed with the classic
 * rail. Mounted by Screen (chrome.tsx). */
export function MobileBottomNav({ active }: { active?: string }) {
  const nav = useNav();
  const u = useUser();
  const tenant = useTenant();
  const hidden = navHiddenForRole(u.role);
  const lbl = useTenantLabels();
  const t = useT();
  const served = new Set(useNavComposition()?.railItemIds ?? []);
  const profile = navProfileForUser(u, tenant?.slug ?? null);
  const items: RailItem[] = profile
    ? profile.items.filter((i) => _railCan(i.needs)).map((i) => ({ id: i.id, label: railLabel(t, i.labelKey, i.label), labelKey: i.labelKey, svg: PROFILE_ICONS[i.icon] ?? <Icon.grid />, group: i.bottom ? "profile-bottom" : "profile-top", badge: i.badge }))
    : RAIL_ITEMS.filter((i) => _railCan(i.needs) && !hidden.has(i.id) && (!i.gated || served.has(i.id))).map((i) => ({ ...i, label: railLabel(t, i.labelKey, i.tenantLabelKey ? lbl(i.tenantLabelKey, i.label) : i.label) }));
  const pKey = pinsKey(tenant?.slug ?? null, profile ? (profile.role || profile.landing) : null);
  const servedDefaults = defaultPinsFor(profile, items);
  const { pinIds, setPins, togglePin } = useNavPins(pKey, items, servedDefaults);
  const { recents, recordRecent } = useNavPrefs(pKey);
  const [launcher, setLauncher] = useState(false);
  const openLauncher = () => { setLauncher(true); try { const url = new URL(location.href); if (!url.searchParams.has("launcher")) { url.searchParams.set("launcher", "1"); history.pushState({ cdLauncher: 1 }, "", url.toString()); } } catch { /* best-effort */ } };
  const closeLauncher = () => { setLauncher(false); try { if (history.state && (history.state as { cdLauncher?: number }).cdLauncher) { history.back(); } else { const url = new URL(location.href); if (url.searchParams.has("launcher")) { url.searchParams.delete("launcher"); history.replaceState(history.state, "", url.toString()); } } } catch { /* best-effort */ } };
  useEffect(() => {
    const onPop = () => { try { if (!new URL(location.href).searchParams.has("launcher")) setLauncher(false); } catch { /* noop */ } };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [comingSoon, setComingSoon] = useState<string | null>(null);
  const barItems: RailItem[] = [];
  pinIds.slice(0, 4).forEach((id) => { const f = items.find((i) => i.id === id); if (f) barItems.push(f); });
  const goItem = (i: RailItem) => { if (i.comingSoon) { setComingSoon(i.label); return; } const r = railRoute(i.id); if (r) { recordRecent(i.id); nav.go(r); if (launcher) closeLauncher(); } };
  /* THE SCAN TAB (KP, 2026-09-17; reordered 2026-09-18): permanent on the phone
   * rail for anyone who holds My Day, and it sits in the CENTRE of the bar as a
   * raised button — My Day, then My Calendar, then Scan, then the remaining
   * pins. It routes to My Day with `?scan=<nonce>` so the board opens its
   * scanner from whatever screen the member is on. */
  const mydayId = barItems.some((i) => i.id === "wf-myday") ? "wf-myday" : barItems.some((i) => i.id === "so-my-day") ? "so-my-day" : barItems.some((i) => i.id === "ev-myday") ? "ev-myday" : null;
  /* Scan opens the My Day this member is actually served (CC, 2026-10-03): the workforce My
   * Day when the served items include it, else the events My Day (its board scans event
   * check-in codes), else no Scan tab. It used to open the workforce My Day for anyone
   * holding myschedule.read, whatever the served profile. */
  const scanTarget = _railCan("myschedule.read") && items.some((i) => i.id === "wf-myday" || i.id === "so-my-day") ? "wf-myday"
    : items.some((i) => i.id === "ev-myday") ? "ev-myday" : null;
  const scanRoute = scanTarget ? railRoute(scanTarget) : null;
  if (scanRoute) {
    const lead = [mydayId, "wf-mysched", "ev-mycal", "so-my-schedule"].filter((id): id is string => !!id);
    barItems.sort((a, b) => { const ai = lead.indexOf(a.id), bi = lead.indexOf(b.id); return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi); });
  }
  const scanTabAt = scanRoute ? Math.min(2, barItems.length) : -1;
  const scanTab = scanRoute ? (
    // ui-raw: mobile bottom-bar tab — the one raised control: a 44px brand-purple disc on a white ring, nudged 6px above the other icons but inside the bar, label beneath.
    <button key="scan" onClick={() => { if (scanRoute) nav.go(scanRoute, { scan: String(Date.now()) }); }} aria-label={t("chrome.rail.scanCode", "Scan a check-in code")} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, border: "none", background: "transparent", cursor: "pointer", color: T.p1, fontFamily: T.font, padding: 0, position: "relative" }}>
      <span style={{ width: 44, height: 44, marginTop: -6, borderRadius: "50%", background: T.p1, color: T.onColor, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `4px solid ${T.white}`, boxShadow: `0 4px 14px ${alpha(T.p1, 35)}` }}><Icon.qr size={20} /></span>
      <span style={{ fontSize: 10, fontWeight: 700 }}>{t("chrome.rail.scan", "Scan")}</span>
    </button>
  ) : null;
  return (
    <>
      <nav aria-label={t("shell.rail.primaryNavigation", "Primary navigation")} style={{ flexShrink: 0, height: 64, background: T.white, borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "stretch", zIndex: 50, overflow: "visible" }}>
        {barItems.map((i, idx) => {
          const on = railHit(active, i.id, nav.route);
          return (
            <Fragment key={i.id}>
            {idx === scanTabAt ? scanTab : null}
            {/* ui-raw: mobile bottom-bar tab — icon stacked over a 10px label, equal flex width.
                Button is a single-line inline-flex control on a fixed height scale. */}
            <button aria-current={on ? "page" : undefined} onClick={() => goItem(i)} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, border: "none", background: "transparent", cursor: "pointer", position: "relative", color: on ? T.p1 : T.n2, fontFamily: T.font, padding: 0 }}>
              <span style={{ display: "inline-flex" }}>{i.svg}</span>
              <span style={{ fontSize: 10, fontWeight: on ? 700 : 500, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "0 2px" }}>{i.label}</span>
              {i.badge ? <span aria-label={i18nT("chrome.tooltips.itemBadgePending.icu", "{value0} pending", { value0: i.badge })} style={{ position: "absolute", top: 9, left: "calc(50% + 6px)", width: 7, height: 7, borderRadius: "50%", background: T.p1, border: `1.5px solid ${T.white}` }} /> : null}
            </button>
            </Fragment>
          );
        })}
        {barItems.length <= scanTabAt ? scanTab : null}
        {/* ui-raw: mobile bottom-bar tab — icon stacked over a 10px label, equal flex width.
            Button is a single-line inline-flex control on a fixed height scale. */}
        <button onClick={openLauncher} aria-label={t("chrome.launcher.allFeatures", "All features")} aria-haspopup="dialog" style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, border: "none", background: "transparent", cursor: "pointer", color: launcher ? T.p1 : T.n2, fontFamily: T.font, padding: 0 }}>
          <JrniMark size={22} />
          <span style={{ fontSize: 10, fontWeight: launcher ? 700 : 500 }}>{t("chrome.launcher.all", "All")}</span>
        </button>
      </nav>
      {launcher ? <NavLauncher mobile items={items} pins={pinIds} recents={recents} isActive={(id) => railHit(active, id, nav.route)} onTogglePin={togglePin} onReorderPins={setPins} onGo={goItem} onClose={closeLauncher} /> : null}
      {comingSoon ? <ComingSoonModal title={comingSoon} onClose={() => setComingSoon(null)} /> : null}
    </>
  );
}
